import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import type { ModalFeedback } from './gpuBudgetFeedback';
import { otherFieldErrors } from './gpuBudgetFeedback';

interface GpuBudgetConfirmModalProps {
  /** Modal title, e.g. "Delete budget". */
  title: string;
  /** The question, e.g. "Delete the budget “Q4”? Its lines are deleted with it." */
  message: React.ReactNode;
  /** The danger button, e.g. "Delete" / "Remove". */
  confirmLabel: string;
  /** Runs the write; the tab decides whether the modal closes. */
  onConfirm: () => Promise<ModalFeedback>;
  onClose: () => void;
}

/**
 * The confirmation of a destructive budget write — deleting a budget, removing a line. The danger
 * button spins while the write is pending; a refusal that keeps the modal open is shown in it.
 * Mounted only while open, so each opening starts clean.
 */
const GpuBudgetConfirmModal: React.FunctionComponent<GpuBudgetConfirmModalProps> = ({
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}) => {
  const [pending, setPending] = useState(false);
  const [alert, setAlert] = useState<string[]>([]);

  const handleConfirm = async () => {
    setPending(true);
    setAlert([]);
    const feedback = await onConfirm();
    if (feedback.close) {
      // The modal unmounts: no state update after this.
      onClose();
      return;
    }
    setPending(false);
    setAlert([...(feedback.alert !== null ? [feedback.alert] : []), ...otherFieldErrors(feedback.fieldErrors, [])]);
  };

  return (
    <Modal variant="small" isOpen onClose={pending ? undefined : onClose} aria-labelledby="gpu-budget-confirm-title">
      <ModalHeader title={title} labelId="gpu-budget-confirm-title" titleIconVariant="warning" />
      <ModalBody>
        {alert.length > 0 && (
          <Alert variant="danger" isInline title={`Could not ${confirmLabel.toLowerCase()}`} className="pf-v6-u-mb-md">
            {alert.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </Alert>
        )}
        <Content component="p">{message}</Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleConfirm()} isLoading={pending} isDisabled={pending}>
          {confirmLabel}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={pending}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GpuBudgetConfirmModal;
